import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { adminApi } from '../../lib/api';
import { formatCurrency, formatDateTime, getPaymentStatusClass, getCheckInStatusClass, CHECK_IN_STATUS_LABELS, PAYMENT_STATUS_LABELS, getTodayString, getDateDaysAgo } from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Download, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { LoadingBlock, EmptyState } from '../../components/common/States';

export default function CheckInsPage() {
  const navigate = useNavigate();
  const [checkins, setCheckIns] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    search: '',
    date_from: getDateDaysAgo(30),
    date_to: getTodayString(),
    payment_status: 'all',
    check_in_status: 'all',
    source: 'all',
    page: 1,
    limit: 20,
  });

  const fetchCheckIns = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (params.payment_status === 'all') delete params.payment_status;
      if (params.check_in_status === 'all') delete params.check_in_status;
      if (params.source === 'all') delete params.source;
      if (!params.search) delete params.search;
      const res = await adminApi.getCheckIns(params);
      setCheckIns(res.data.checkins);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error('Failed to load check-ins'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchCheckIns(); }, [fetchCheckIns]);

  const handleFilter = (key, val) => setFilters(f => ({ ...f, [key]: val, page: 1 }));

  return (
    <AdminShell
      title="Check-ins"
      actions={
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40 text-xs"
          onClick={() => adminApi.exportCheckIns(filters.date_from, filters.date_to)}
          data-testid="export-checkins-button"
        >
          <Download size={13} /> Export CSV
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative lg:col-span-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6F6F6F]" />
              <Input
                placeholder="Name, phone, ref..."
                value={filters.search}
                onChange={e => handleFilter('search', e.target.value)}
                className="bg-[#111111] border-[#2A2A2A] pl-9 text-sm"
                data-testid="admin-checkins-search"
              />
            </div>
            <Select value={filters.payment_status} onValueChange={v => handleFilter('payment_status', v)}>
              <SelectTrigger className="bg-[#111111] border-[#2A2A2A] text-sm" data-testid="admin-checkins-payment-status-filter">
                <SelectValue placeholder="Payment status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="not_initiated">Not Initiated</SelectItem>
                <SelectItem value="not_required">Not Required</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.check_in_status} onValueChange={v => handleFilter('check_in_status', v)}>
              <SelectTrigger className="bg-[#111111] border-[#2A2A2A] text-sm" data-testid="admin-checkins-status-filter">
                <SelectValue placeholder="Check-in status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="arrived">Arrived</SelectItem>
                <SelectItem value="checked_in">Checked In</SelectItem>
                <SelectItem value="no_show">No Show</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input
                type="date"
                value={filters.date_from}
                onChange={e => handleFilter('date_from', e.target.value)}
                className="bg-[#111111] border-[#2A2A2A] text-sm flex-1"
              />
              <Input
                type="date"
                value={filters.date_to}
                onChange={e => handleFilter('date_to', e.target.value)}
                className="bg-[#111111] border-[#2A2A2A] text-sm flex-1"
              />
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center justify-between">
          <p className="text-[#B9B9B9] text-sm">
            <span className="text-[#F2F2F2] font-medium">{total}</span> check-ins found
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              disabled={filters.page <= 1}
              onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
              className="h-7 w-7 text-[#B9B9B9]"
            >
              <ChevronLeft size={15} />
            </Button>
            <span className="text-[#B9B9B9] text-xs">{filters.page} / {pages || 1}</span>
            <Button
              variant="ghost"
              size="icon"
              disabled={filters.page >= pages}
              onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
              className="h-7 w-7 text-[#B9B9B9]"
            >
              <ChevronRight size={15} />
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="surface-card card-highlight rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-4"><LoadingBlock rows={8} /></div>
          ) : checkins.length === 0 ? (
            <EmptyState title="No check-ins found" description="Try adjusting your filters." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#2A2A2A]">
                    {['Ref', 'Guest', 'Phone', 'Guests', 'Date', 'Promoter', 'Check-in', 'Payment', 'Amount', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs text-[#6F6F6F] uppercase tracking-wider font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E1E1E]">
                  {checkins.map(ci => (
                    <tr
                      key={ci._id}
                      className="table-row-hover cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/checkins/${ci._id}`)}
                    >
                      <td className="px-4 py-3 text-mono text-[#D4A843] text-xs">{ci.reference_id}</td>
                      <td className="px-4 py-3 text-[#F2F2F2] font-medium">{ci.customer_name}</td>
                      <td className="px-4 py-3 text-[#B9B9B9]">{ci.phone}</td>
                      <td className="px-4 py-3 text-[#B9B9B9] text-center">{ci.guests}</td>
                      <td className="px-4 py-3 text-[#B9B9B9]">{ci.visit_date}</td>
                      <td className="px-4 py-3">
                        {ci.promoter ? (
                          <div className="flex flex-col leading-tight">
                            <span
                              className="font-mono text-[#D4A843] text-sm tabular-nums"
                              title={`${ci.promoter.code} · ${ci.promoter.name || 'Unnamed'}`}
                              data-testid={`checkin-promoter-${ci.reference_id}`}
                            >
                              {ci.promoter.short_code || ci.promoter.code}
                            </span>
                            <span className="text-[10px] text-[#6F6F6F] truncate max-w-[110px]">
                              {ci.promoter.name || ci.promoter.code}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[#3A3A3A] text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getCheckInStatusClass(ci.check_in_status)}`}>
                          {CHECK_IN_STATUS_LABELS[ci.check_in_status] || ci.check_in_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getPaymentStatusClass(ci.payment_status)}`}>
                          {PAYMENT_STATUS_LABELS[ci.payment_status] || ci.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#D4A843] font-medium">{formatCurrency(ci.total_amount)}</td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs text-[#B9B9B9] hover:text-[#F2F2F2] h-6 px-2"
                          onClick={e => { e.stopPropagation(); navigate(`/admin/checkins/${ci._id}`); }}
                        >View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
